import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FiCheckCircle,
  FiMail,
  FiTruck,
  FiClock,
  FiArrowLeft,
  FiPackage,
  FiUser,
  FiMapPin,
} from "react-icons/fi";
import Particles from "../../../components/utils/Particles";
import styled, { keyframes } from "styled-components";
import { formatPrice } from "../../../utils/utils";

const OrderSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Dobijanje podataka iz state-a
  const orderData = location.state?.orderData;

  // Fallback vrednosti ako nema podataka
  const {
    orderNumber = "#12345",
    total = 0,
    items = 0,
    customerInfo = {},
  } = orderData || {};

  const {
    firstName = "",
    lastName = "",
    email = "",
    phone = "",
    address = "",
    city = "",
    zipCode = "",
  } = customerInfo;

  useEffect(() => {
    // Scroll to top na mount
    window.scrollTo(0, 0);

    // Redirect na početnu ako nema podataka o porudžbini
    if (!orderData) {
      setTimeout(() => {
        navigate("/");
      }, 3000);
    }
  }, [orderData, navigate]);

  // Ako nema podataka, prikaži poruku o redirectu
  if (!orderData) {
    return (
      <>
        <Particles />
        <Container>
          <SuccessIcon>
            <FiCheckCircle />
          </SuccessIcon>
          <MainTitle>Nema podataka o porudžbini</MainTitle>
          <Subtitle>Vraćamo vas na početnu stranicu...</Subtitle>
        </Container>
      </>
    );
  }

  return (
    <>
      <Particles />
      <Container>
        <SuccessIcon>
          <FiCheckCircle />
        </SuccessIcon>

        <MainTitle>Hvala vam na porudžbini!</MainTitle>

        <Subtitle>
          Vaša porudžbina je uspešno primljena i uskoro će biti obrađena.
        </Subtitle>

        <InfoGrid>
          <InfoCard $delay={0}>
            <CardIcon>
              <FiMail />
            </CardIcon>
            <CardTitle>Email potvrda</CardTitle>
            <CardText>
              Poslali smo vam email potvrdu na adresu{" "}
              <strong style={{ color: "#ff6b35" }}>{email}</strong> sa detaljima
              vaše porudžbine. Proverite i spam folder ukoliko ne vidite poruku.
            </CardText>
          </InfoCard>

          <InfoCard $delay={1}>
            <CardIcon>
              <FiClock />
            </CardIcon>
            <CardTitle>Obrada porudžbine</CardTitle>
            <CardText>
              Naš tim će obraditi vašu porudžbinu u roku od 24-48 sati. Bićete
              obavešteni o statusu preko email-a.
            </CardText>
          </InfoCard>

          <InfoCard $delay={2}>
            <CardIcon>
              <FiTruck />
            </CardIcon>
            <CardTitle>Dostava</CardTitle>
            <CardText>
              Nakon obrade, porudžbina će biti poslata na adresu koju ste
              naveli. Očekivano vreme dostave je 3-5 radnih dana.
            </CardText>
          </InfoCard>
        </InfoGrid>

        <OrderSummary>
          <h3
            style={{
              color: "#ff6b35",
              marginBottom: "1.5rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <FiPackage /> Rezime porudžbine
          </h3>

          <SummaryGrid>
            <SummaryCard>
              <SummaryTitle>
                <FiPackage /> Detalji porudžbine
              </SummaryTitle>
              <DetailRow>
                <span className="label">Broj porudžbine:</span>
                <span className="value">{orderNumber}</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Ukupno stavki:</span>
                <span className="value">{items}</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Ukupna cena:</span>
                <span className="value total">{formatPrice(total)}</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Datum:</span>
                <span className="value">
                  {new Date().toLocaleDateString("sr-Latn-RS", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </DetailRow>
              <DetailRow>
                <span className="label">Status:</span>
                <span className="value status">U obradi</span>
              </DetailRow>
            </SummaryCard>

            <SummaryCard>
              <SummaryTitle>
                <FiUser /> Podaci kupca
              </SummaryTitle>
              <DetailRow>
                <span className="label">Ime i prezime:</span>
                <span className="value">
                  {firstName} {lastName}
                </span>
              </DetailRow>
              <DetailRow>
                <span className="label">Email:</span>
                <span className="value">{email}</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Telefon:</span>
                <span className="value">{phone}</span>
              </DetailRow>
            </SummaryCard>

            <SummaryCard>
              <SummaryTitle>
                <FiMapPin /> Adresa dostave
              </SummaryTitle>
              <DetailRow>
                <span className="label">Adresa:</span>
                <span className="value">{address}</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Grad:</span>
                <span className="value">{city}</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Poštanski broj:</span>
                <span className="value">{zipCode}</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Očekivana dostava:</span>
                <span className="value">3-5 radnih dana</span>
              </DetailRow>
            </SummaryCard>
          </SummaryGrid>
        </OrderSummary>

        <ActionButtons>
          <Button
            onClick={() => {
              navigate("/");
              setTimeout(() => {
                window.scrollTo(0, 0);
              }, 300);
            }}
          >
            <FiArrowLeft />
            Nazad na početnu
          </Button>

          <Button
            $primary
            onClick={() => {
              navigate("/prodavnica");
              setTimeout(() => {
                window.scrollTo(0, 0);
              }, 300);
            }}
          >
            <FiPackage />
            Nastavi kupovinu
          </Button>
        </ActionButtons>
      </Container>
    </>
  );
};

export default OrderSuccessPage;

// Animacije
const checkmarkScale = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(255, 107, 53, 0.4);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(255, 107, 53, 0);
  }
`;

// Styled komponente
const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  text-align: center;
  width: 100%;
  margin-top: 5rem;
`;

const SuccessIcon = styled.div`
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #27ae60, #2ecc71);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  animation: ${checkmarkScale} 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    animation: ${pulse} 2s infinite;
  }

  svg {
    color: white;
    font-size: 3rem;
    animation: ${float} 3s ease-in-out infinite;
  }
`;

const MainTitle = styled.h1`
  color: #ff6b35;
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ff6b35, #ff8c42);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${slideInUp} 0.8s ease-out 0.2s both;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: #ffffff;
  font-size: 1.2rem;
  margin-bottom: 3rem;
  opacity: 0.9;
  animation: ${slideInUp} 0.8s ease-out 0.4s both;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  animation: ${slideInUp} 0.8s ease-out ${(props) => 0.6 + props.$delay * 0.2}s
    both;

  &:hover {
    border-color: #ff6b35;
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(255, 107, 53, 0.1);
  }
`;

const CardIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #ff6b35, #e55a2b);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;

  svg {
    color: white;
    font-size: 1.5rem;
  }
`;

const CardTitle = styled.h3`
  color: #ff6b35;
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

const CardText = styled.p`
  color: #cccccc;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const OrderSummary = styled.div`
  background: #2c2c2c;
  border: 1px solid #444;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 2rem;
  animation: ${slideInUp} 0.8s ease-out 1.2s both;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  text-align: left;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 1.5rem;
`;

const SummaryTitle = styled.h4`
  color: #ff6b35;
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #333;

  &:last-child {
    border-bottom: none;
  }

  .label {
    color: #cccccc;
    font-size: 0.9rem;
  }

  .value {
    color: #ffffff;
    font-weight: 500;
    font-size: 0.9rem;

    &.total {
      color: #27ae60;
      font-weight: bold;
      font-size: 1.1rem;
    }

    &.status {
      color: #ff6b35;
      font-weight: bold;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  animation: ${slideInUp} 0.8s ease-out 1.4s both;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  background: ${(props) =>
    props.$primary
      ? "linear-gradient(135deg, #ff6b35, #e55a2b)"
      : "transparent"};
  color: ${(props) => (props.$primary ? "#ffffff" : "#ff6b35")};
  border: ${(props) => (props.$primary ? "none" : "2px solid #ff6b35")};
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 107, 53, 0.3);
    background: ${(props) =>
      props.$primary ? "linear-gradient(135deg, #e55a2b, #ff6b35)" : "#ff6b35"};
    color: #ffffff;
  }

  &:active {
    transform: translateY(0);
  }
`;
